"use strict";
/** *******************************************************************************************************************
  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
  
  Licensed under the Apache License, Version 2.0 (the "License").
  You may not use this file except in compliance with the License.
  You may obtain a copy of the License at
  
      http://www.apache.org/licenses/LICENSE-2.0
  
  Unless required by applicable law or agreed to in writing, software
  distributed under the License is distributed on an "AS IS" BASIS,
  WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
  See the License for the specific language governing permissions and
  limitations under the License.                                                                              *
 ******************************************************************************************************************** */
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = __importStar(require("react"));
const clsx_1 = __importDefault(require("clsx"));
const core_1 = require("@material-ui/core");
const ReportProblemOutlined_1 = __importDefault(require("@material-ui/icons/ReportProblemOutlined"));
const Button_1 = __importDefault(require("../Button"));
const Box_1 = __importDefault(require("../../layouts/Box"));
const ExpandableSection_1 = __importDefault(require("../ExpandableSection"));
const Link_1 = __importDefault(require("../Link"));
const useStyles = core_1.makeStyles((theme) => ({
    formFieldRoot: {
        '&:not(:last-child)': {
            marginBottom: '20px',
        },
    },
    formFieldControls: {
        marginTop: '5px',
    },
    errorText: {
        display: 'flex',
        alignItems: 'center',
        color: theme.palette.error.dark,
    },
    expandable: {
        marginLeft: theme.spacing(2),
    },
    secondaryControl: {
        display: 'flex',
        alignItems: 'center',
        '& > *': {
            marginLeft: theme.spacing(1),
        },
    },
}));
const ErrorText = ({ errorText }) => {
    const classes = useStyles();
    return (react_1.default.createElement(core_1.Typography, { variant: "subtitle1", component: "div", className: clsx_1.default(classes.errorText, classes.formFieldControls) },
        react_1.default.createElement(ReportProblemOutlined_1.default, { fontSize: "small" }),
        react_1.default.createElement("span", null, errorText)));
};
const Control = ({ stretch, children, secondaryControl, renderReload, onReloadClick, createNewLink, createNewLinkHref, }) => {
    const classes = useStyles();
    return (react_1.default.createElement(core_1.Grid, { container: true, spacing: 1, alignItems: "center" },
        react_1.default.createElement(core_1.Grid, { item: true, xs: 12, sm: stretch ? 12 : 9 }, children),
        (secondaryControl || renderReload || createNewLinkHref) && (react_1.default.createElement(core_1.Grid, { item: true, xs: 12, sm: stretch ? 12 : 3 }, renderReload || createNewLinkHref ? (react_1.default.createElement(Box_1.default, { className: classes.secondaryControl },
            renderReload && (react_1.default.createElement(Button_1.default, { label: "Reload", icon: "refresh", onClick: onReloadClick }, "Reload")),
            createNewLinkHref && createNewLink && (react_1.default.createElement(Link_1.default, { href: createNewLinkHref, forceExternal: true }, createNewLink)))) : (secondaryControl)))));
};
/**
 * A form field is a combination of various patterns, making it easy to create properly styled controls in a form.
 */
const FormField = ({ children, controlId, description, errorText, hintText, label, secondaryControl, renderReload = false, onReloadClick, createNewLink, createNewLinkHref, stretch = false, expandable = false, footer, }) => {
    const classes = useStyles();
    const content = react_1.useMemo(() => {
        return (react_1.default.createElement(Box_1.default, { className: clsx_1.default({ [classes.expandable]: expandable }) },
            description && (react_1.default.createElement(core_1.Typography, { variant: "subtitle1", component: "div" }, description)),
            react_1.default.createElement("div", { className: classes.formFieldControls },
                react_1.default.createElement(Control, { stretch: stretch, secondaryControl: secondaryControl, createNewLink: createNewLink, createNewLinkHref: createNewLinkHref, renderReload: renderReload, onReloadClick: onReloadClick }, children)),
            errorText && react_1.default.createElement(ErrorText, { errorText: errorText }),
            hintText && react_1.default.createElement(core_1.FormHelperText, { className: classes.formFieldControls }, hintText),
            footer && react_1.default.createElement(Box_1.default, { marginTop: 1 }, footer)));
    }, [
        description,
        stretch,
        footer,
        children,
        errorText,
        hintText,
        classes,
        secondaryControl,
        expandable,
        createNewLink,
        createNewLinkHref,
        renderReload,
        onReloadClick,
    ]);
    const header = react_1.useMemo(() => {
        return react_1.default.createElement(react_1.default.Fragment, null, label && react_1.default.createElement(core_1.InputLabel, { htmlFor: controlId }, label));
    }, [label, controlId]);
    if (!expandable) {
        return (react_1.default.createElement(core_1.FormControl, { className: classes.formFieldRoot },
            header,
            content));
    }
    return (react_1.default.createElement(ExpandableSection_1.default, { variant: "borderless", header: header, expanded: !!errorText }, content));
};
exports.default = FormField;
//# sourceMappingURL=index.js.map